import type {FunctionComponent, SyntheticEvent} from 'react'
import React, {useMemo} from 'react'
import {Link, useOutletContext, useRouteLoaderData, useSubmit} from '@remix-run/react'
import clsx from 'clsx'
import {slugToText} from '@roundforest/text-transforms-commons'
import {AmazonLogoIcon} from '~/icons/amazon-logo-icon'
import type {DealOfTheDay, RootAndListContext, RootLoaderResponse} from '~/types'
import {getDomainConfig} from '~/domains'
import {getTranslation} from '~/localization/translation'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
import {useReportItemSeen, useSendPartnerProductClickoutCustom, useReportWidgetClick} from '~/hooks/analytic-hooks'
import {getSchemaPriceValidUntil, objectToQueryString} from '~/utils/commons'
import {grantAllConsent} from '~/utils/consent-manager.client'
import DiscountCornerBadge from '../../badges/discount-corner-ribbon'

interface TopDealsCardProps {
  productInfo: DealOfTheDay
  index: number
}

const TopDealsCard: FunctionComponent<TopDealsCardProps> = ({productInfo, index}) => {
  const {language, currencyCode, domain, locale, featureFlags, trackAnalytics, hostname, traffic, renderTimestamp} =
    useRouteLoaderData('root') as RootLoaderResponse
  const {styles: stylesBydomain} = getDomainConfig(domain, locale)
  const translations = getTranslation(language)
  const {image, title, oldPrice, price, discount, slug, affiliateLink, placement, asin} = productInfo
  const {clickId, setShowCookieConsentGdpr} = useOutletContext<RootAndListContext>()
  const submit = useSubmit()
  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(language, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
      }),
    [language, currencyCode],
  )

  const [cardRef] = useReportItemSeen({
    item_list_id: slug,
    item_name: title,
    item_source: 'list',
    partner_item_brand: title,
    partner_item_id: asin,
    affiliation: affiliateLink.type,
    is_dws: false,
    price,
    currency: currencyCode,
    click_element_name: 'deals-of-the-day',
    index: index + 1,
  })

  const reportWidgetClick = useReportWidgetClick()

  const reportClickout = useSendPartnerProductClickoutCustom()

  const shouldForceTrackAnalytics = Boolean(
    featureFlags?.cookieConsentVariation === 'opt-in' &&
    (!trackAnalytics?.permission || trackAnalytics?.permission === 'denied'),
  )

  const trackSearchParams = objectToQueryString({
    session_id: trackAnalytics?.session_id,
    ...trackAnalytics?.pageLoadData,
    page_id: trackAnalytics?.page_id,
    user_id: trackAnalytics?.user_id,
  })

  const affiliateLinkWithClickId = affiliateLink.url.replaceAll(CLICK_ID_PLACEHOLDER, clickId)

  const redirectUrl = shouldForceTrackAnalytics
    ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId)}&${trackSearchParams}`
    : affiliateLinkWithClickId

  const handleCardClick = (_e: SyntheticEvent, elementName: 'image' | 'title' | 'price' | 'cta') => {
    if (shouldForceTrackAnalytics) {
      setShowCookieConsentGdpr(false)
      grantAllConsent()
      submit(
        {
          'accept-gdpr': 'granted',
          redirect: 'true',
          'track-analytics-data': JSON.stringify({...trackAnalytics, permission: 'granted'}),
        },
        {method: 'POST', action: '/cookie-consent', replace: false, navigate: false},
      )
    }

    reportClickout({
      index: index + 1,
      item_list_id: slug,
      click_element_name: `deals-of-the-day-card-${index + 1}`,
      partner_item_brand: title,
      partner_item_id: asin,
      item_name: title,
      item_brand: title,
      click_id: affiliateLink?.clickId,
      affiliation: affiliateLink?.type,
      is_dws: true,
      location_id: locale,
      traffic: traffic || 'unknown',
      gclid: undefined,
      sys_id: undefined,
      target: affiliateLinkWithClickId,
    })
    reportWidgetClick({
      widget_name: `deals-of-the-day-card`,
      widget_place: `${index + 1}`,
      trigger_type: elementName,
    })

    if (typeof Storage != 'undefined') {
      localStorage.setItem('clickTime', Date.now().toString())
      if (featureFlags?.showHustonProblemPopup) localStorage.setItem('redirectUrl', redirectUrl)
    }
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Product',
            name: title,
            image: [image],
            description: title,
            brand: {
              '@type': 'Brand',
              name: title,
            },
            offers: {
              '@type': 'Offer',
              url: `https://${hostname}${affiliateLinkWithClickId}`,
              priceCurrency: currencyCode,
              price: price,
              priceValidUntil: getSchemaPriceValidUntil(renderTimestamp, 'en-CA'),
              itemCondition: 'https://schema.org/NewCondition',
              availability: 'https://schema.org/InStock',
              seller: {
                '@type': 'Organization',
                name: title ?? 'Amazon',
              },
            },
          }),
        }}
      ></script>

      <div
        className="relative flex min-h-80 w-60 min-w-60 flex-col rounded-xl border border-[#D5D9D9] bg-white"
        ref={cardRef}
      >
        {discount && discount > 0 ? (
          <DiscountCornerBadge className="absolute -left-1 -top-1 w-full" discount={discount} />
        ) : null}
        <AmazonLogoIcon className="absolute right-4 top-2 h-6 w-16 object-scale-down" />
        <div className="mt-7 flex flex-col items-center justify-center gap-2 px-2 py-3">
          <div className="relative flex size-32 items-center justify-center">
            <a
              className="absolute inset-0"
              href={redirectUrl}
              onClick={(e) => handleCardClick(e, 'image')}
              aria-label={`view ${slug}`}
              target="_blank"
              rel="sponsored noreferrer"
            ></a>
            <img
              src={image}
              alt={`${productInfo.title}`}
              className="size-32 object-scale-down"
              loading="eager"
              width="128"
              height="128"
            />
          </div>
          <span className="relative line-clamp-1 text-start text-base font-semibold">
            <a
              className="absolute inset-0"
              href={redirectUrl}
              onClick={(e) => handleCardClick(e, 'title')}
              aria-label={`view ${slug}`}
              target="_blank"
              rel="sponsored noreferrer"
            ></a>
            {productInfo.title}
          </span>
          <Link
            to={`/${slug}`}
            prefetch="intent"
            aria-label={`view ${slugToText(slug)}`}
            className="self-start rounded-lg bg-[#EFF5FF] px-1.5 py-0.5 text-sm font-light capitalize text-[#18297C] underline"
          >
            {translations.Home.Deals.bestIn(placement, slugToText(slug))}
          </Link>
        </div>
        <div className="border-t-dotted mt-auto flex w-full flex-col justify-center gap-1 border-t border-[#E3E3E3] p-3">
          <div className="relative flex flex-row items-center justify-center gap-1">
            <a
              className="absolute inset-0"
              href={redirectUrl}
              onClick={(e) => handleCardClick(e, 'price')}
              aria-label={`view ${slug}`}
              target="_blank"
              rel="sponsored noreferrer"
            ></a>
            {price ? <span className="text-[22px] text-[#FE282E]">{currencyFormatter.format(price)}</span> : null}
            {oldPrice ? (
              <span className="text-[15px] font-light text-[#999999] line-through">
                {currencyFormatter.format(oldPrice)}
              </span>
            ) : null}
          </div>
          <a
            href={redirectUrl}
            target="_blank"
            rel="sponsored noreferrer"
            onClick={(e) => handleCardClick(e, 'cta')}
            className={clsx(
              'w-full rounded-[50px] p-2 text-center text-[13px] font-normal uppercase',
              stylesBydomain.HOMEPAGE_BUTTON_PRIMARY,
              'homepage_button_primary',
            )}
          >
            {translations.Home.viewDeal}
          </a>
        </div>
      </div>
    </>
  )
}

export default TopDealsCard
